// src/lib/apkUpload.js
import { ref, uploadBytesResumable, getDownloadURL, listAll, deleteObject, getBlob, getMetadata, uploadBytes } from "firebase/storage";
import { storage } from "@/lib/firebase";

/**
 * Upload an APK file to Firebase Storage
 * @param {File} apkFile - The APK file to upload
 * @param {string} version - Version name (e.g., "1.0.0")
 * @param {Function} onProgress - Progress callback function
 * @returns {Promise<{downloadURL: string, storagePath: string, version: string}>}
 */
export async function uploadAPK(apkFile, version, onProgress) {
  if (!apkFile) throw new Error("No APK file selected");
  if (!version) throw new Error("Version is required");

  // Validate file type
  if (!apkFile.name.endsWith('.apk')) {
    throw new Error("File must be an APK file");
  }

  // Optional: size limit (100MB)
  const MAX_SIZE = 100 * 1024 * 1024;
  if (apkFile.size > MAX_SIZE) {
    throw new Error("APK file too large. Max 100MB.");
  }

  // Create safe filename with version
  const timestamp = new Date().toISOString().replace(/[:.]/g, '-');
  const safeName = `app-v${version}-${timestamp}.apk`;
  const storagePath = `apk/${safeName}`;
  const storageRef = ref(storage, storagePath);

  // Set metadata
  const metadata = {
    contentType: 'application/vnd.android.package-archive',
    customMetadata: {
      version: version,
      uploadDate: new Date().toISOString(),
      originalName: apkFile.name
    }
  };

  const task = uploadBytesResumable(storageRef, apkFile, metadata);

  await new Promise((resolve, reject) => {
    task.on(
      "state_changed",
      (snapshot) => {
        if (onProgress) {
          const progress = Math.round((snapshot.bytesTransferred / snapshot.totalBytes) * 100);
          onProgress(progress);
        }
      },
      reject,
      resolve
    );
  });

  const downloadURL = await getDownloadURL(storageRef);
  
  return {
    downloadURL,
    storagePath,
    version,
    fileName: safeName,
    size: apkFile.size,
    uploadDate: new Date().toISOString()
  };
}

/**
 * Get all uploaded APK files
 * @returns {Promise<Array>} Array of APK file info
 */
export async function listAPKFiles() {
  try {
    const apkRef = ref(storage, 'apk');
    const result = await listAll(apkRef);
    
    const apkFiles = await Promise.all(
      result.items.map(async (itemRef) => {
        const downloadURL = await getDownloadURL(itemRef);
        return {
          name: itemRef.name,
          downloadURL,
          path: itemRef.fullPath,
          // Extract version from filename if possible
          version: extractVersionFromName(itemRef.name)
        };
      })
    );

    // Sort by upload date (newest first)
    return apkFiles.sort((a, b) => b.name.localeCompare(a.name));
  } catch (error) {
    console.error('Error listing APK files:', error);
    throw error;
  }
}

/**
 * Delete an APK file
 * @param {string} fileName - Name of the APK file to delete
 */
export async function deleteAPK(fileName) {
  try {
    const storageRef = ref(storage, `apk/${fileName}`);
    await deleteObject(storageRef);
    return { success: true, fileName };
  } catch (error) {
    console.error('Error deleting APK file:', error);
    throw error;
  }
}

/**
 * Rename an existing APK file by copying and removing the original blob.
 * @param {string} currentName - Existing APK file name inside the apk/ folder
 * @param {string} desiredName - New filename (with or without .apk)
 * @returns {Promise<{name: string, downloadURL: string, path: string, version: string}>}
 */
export async function renameAPK(currentName, desiredName) {
  if (!currentName) throw new Error('Current file name is required');
  if (!desiredName) throw new Error('New file name is required');

  const sanitizedName = sanitizeApkFileName(desiredName);
  if (sanitizedName === currentName) {
    throw new Error('New file name must be different from the current name');
  }

  const currentRef = ref(storage, `apk/${currentName}`);
  const newRef = ref(storage, `apk/${sanitizedName}`);

  // Ensure we are not overwriting an existing file
  try {
    await getMetadata(newRef);
    throw new Error('A file with the requested name already exists');
  } catch (error) {
    if (error?.code !== 'storage/object-not-found') {
      throw error;
    }
  }

  const [blob, metadata] = await Promise.all([
    getBlob(currentRef),
    getMetadata(currentRef)
  ]);

  const uploadMetadata = {};
  if (metadata?.contentType) {
    uploadMetadata.contentType = metadata.contentType;
  }
  if (metadata?.customMetadata) {
    uploadMetadata.customMetadata = metadata.customMetadata;
  }

  await uploadBytes(newRef, blob, uploadMetadata);
  await deleteObject(currentRef);

  const downloadURL = await getDownloadURL(newRef);
  return {
    name: sanitizedName,
    downloadURL,
    path: `apk/${sanitizedName}`,
    version: extractVersionFromName(sanitizedName)
  };
}

/**
 * Extract version number from filename
 * @param {string} fileName - The filename
 * @returns {string} Version number or 'unknown'
 */
function extractVersionFromName(fileName) {
  const match = fileName.match(/v(\d+\.\d+\.\d+)/);
  return match ? match[1] : 'unknown';
}

function sanitizeApkFileName(rawName) {
  let name = rawName.trim();
  if (!name) throw new Error('New file name cannot be empty');

  if (!name.toLowerCase().endsWith('.apk')) {
    name += '.apk';
  }

  name = name.replace(/\s+/g, '-');
  name = name.replace(/[^a-zA-Z0-9_.-]/g, '');

  if (!name.toLowerCase().endsWith('.apk')) {
    name += '.apk';
  }

  const baseName = name.slice(0, -4);
  if (!baseName || baseName.replace(/[-_.]/g, '').length === 0) {
    throw new Error('New file name must include alphanumeric characters');
  }

  return name;
}

/**
 * Get the latest APK download URL
 * @returns {Promise<string|null>} URL of the latest APK or null if none found
 */
export async function getLatestAPKDownloadURL() {
  try {
    const apkFiles = await listAPKFiles();
    if (apkFiles.length === 0) return null;
    
    // Return the first (newest) APK
    return apkFiles[0].downloadURL;
  } catch (error) {
    console.error('Error getting latest APK:', error);
    return null;
  }
}
